import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import HttpService from "../services/httpService";
import {
    Button,
    TextField,
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";

const emptyForm = { text: "", l1User: "", l2User: "", l3User: "", l4User: "" };

const HEADER_BG = "#6B1E4A";
// Fallback only - the real per-tier background comes from the "color" saved
// against each L1/L2/L3/L4 entry in tat-internal-ticket (see getTatColor below).
const FALLBACK_LEVEL_BG = "#e0e0e0";

const EscalationMatrix = (props) => {
    const [entries, setEntries] = useState([]);
    const [techUsers, setTechUsers] = useState([]);
    const [defaultL4User, setDefaultL4User] = useState(null);
    const [tatInternalTickets, setTatInternalTickets] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        fetchEntries();
        fetchTechUsers();
        fetchTatInternalTickets();
        // eslint-disable-next-line
    }, []);

    const fetchEntries = async () => {
        props.setLoading(true);
        try {
            const res = await HttpService.getEscalationMatrixList();
            setEntries(res?.data?.data || []);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    const fetchTechUsers = async () => {
        try {
            const res = await HttpService.getEscalationMatrixTechUsers();
            const users = res?.data?.data || [];
            const l4User = res?.data?.defaultL4User || null;
            // Make sure the default L4 user is selectable even if they aren't in the
            // plain Tech-department list (e.g. a manager from another department),
            // otherwise the pre-filled select would render blank.
            const usersWithDefaultL4 = l4User && !users.some((u) => u._id === l4User._id)
                ? [...users, l4User]
                : users;
            setTechUsers(usersWithDefaultL4);
            // L4 (Escalation) defaults to the Tech team's manager, the same person
            // across every entry - pre-fill it, but it's still editable if needed.
            setDefaultL4User(l4User);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load Tech department users", "error");
        }
    };

    // TAT hours per level (L1-L4), sourced from the tat-internal-ticket collection.
    const fetchTatInternalTickets = async () => {
        try {
            const res = await HttpService.getTatInternalTickets();
            const data = res?.data?.data || [];
            setTatInternalTickets(data);
        } catch (error) {
            console.error('Error fetching tat internal tickets:', error);
        }
    };

    // Loosened to a prefix match (e.g. "L1", "L1 (Primary Owner)") so it still
    // resolves even if the saved Category text has extra words after the level.
    const findTatEntry = (level) => {
        return tatInternalTickets.find((t) => {
            const normalized = t.text?.trim().toUpperCase() || '';
            return normalized === level.toUpperCase() || normalized.startsWith(level.toUpperCase());
        });
    };

    const getTatHours = (level) => {
        return findTatEntry(level)?.hours ?? null;
    };

    // Per-tier background color, taken from that L1/L2/L3/L4 entry's own "color"
    // field in tat-internal-ticket (falls back to a neutral gray if not set).
    const getTatColor = (level) => {
        return findTatEntry(level)?.color || FALLBACK_LEVEL_BG;
    };

    // Auto-pick readable (dark/light) text for whatever background color was chosen.
    const getContrastTextColor = (hexColor) => {
        const hex = (hexColor || '').replace('#', '');
        if (hex.length !== 6) return '#1a1a1a';
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
    };

    const levelHeaderLabel = (level, ownerLabel) => {
        const hours = getTatHours(level);
        return (
            <>
                {level}<br/>({ownerLabel})<br/>{hours !== null ? `TAT- ${hours} Hrs` : 'TAT- N/A'}
            </>
        );
    };

    const openAddForm = () => {
        setFormData({ ...emptyForm, l4User: defaultL4User?._id || "" });
        setEditingId(null);
        setOpenForm(true);
    };

    const openEditForm = (entry) => {
        setFormData({
            text: entry.text,
            l1User: entry.l1User?._id || "",
            l2User: entry.l2User?._id || "",
            l3User: entry.l3User?._id || "",
            l4User: entry.l4User?._id || ""
        });
        setEditingId(entry._id);
        setOpenForm(true);
    };

    const closeForm = () => {
        setOpenForm(false);
        setFormData(emptyForm);
        setEditingId(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // L1/L2/L3/L4 must each be a different person (a level can be left unset, but two
    // levels can't both point at the same user).
    const selectedLevelUsers = [formData.l1User, formData.l2User, formData.l3User, formData.l4User]
        .filter((id) => id);
    const hasDuplicateLevelUsers = new Set(selectedLevelUsers).size !== selectedLevelUsers.length;

    const isFormValid = formData.text.trim() !== "" && !hasDuplicateLevelUsers;

    const handleSubmit = async () => {
        if (hasDuplicateLevelUsers) {
            props.setSnackInfo("L1, L2, L3 and L4 must each be a different person", "warning");
            return;
        }
        if (!isFormValid) {
            props.setSnackInfo("Please fill the issue type/issue category", "warning");
            return;
        }
        props.setLoading(true);
        try {
            const payload = {
                text: formData.text.trim(),
                l1User: formData.l1User || null,
                l2User: formData.l2User || null,
                l3User: formData.l3User || null,
                l4User: formData.l4User || null
            };
            if (editingId) {
                await HttpService.updateEscalationMatrix(editingId, payload);
                props.setSnackInfo("Category updated successfully", "success");
            } else {
                await HttpService.createEscalationMatrix(payload);
                props.setSnackInfo("Category created successfully", "success");
            }
            closeForm();
            fetchEntries();
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
    };

    const handleDelete = async () => {
        props.setLoading(true);
        try {
            await HttpService.deleteEscalationMatrix(deleteId);
            props.setSnackInfo("Category deleted successfully", "success");
            setDeleteId(null);
            fetchEntries();
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error");
        }
        props.setLoading(false);
    };

    const renderUserSelect = (label, name) => {
        const value = formData[name];
        const isDuplicate = !!value && selectedLevelUsers.filter((id) => id === value).length > 1;
        return (
            <FormControl fullWidth margin="dense" variant="outlined" error={isDuplicate}>
                <InputLabel>{label}</InputLabel>
                <Select
                    label={label}
                    name={name}
                    value={value}
                    onChange={handleChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {techUsers.map((user) => (
                        <MenuItem key={user._id} value={user._id}>
                            {user.name}
                        </MenuItem>
                    ))}
                </Select>
                {isDuplicate && <FormHelperText>Already assigned to another level</FormHelperText>}
            </FormControl>
        );
    };

    return (
        <Box style={{ padding: 20 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Escalation Matrix</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddForm}>
                    Add Category
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>SL No.</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>Issue Type/Issue Category</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>{levelHeaderLabel('L1', 'Primary Owner')}</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>{levelHeaderLabel('L2', 'Secondary Owner')}</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>{levelHeaderLabel('L3', 'Tertiary Owner')}</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>{levelHeaderLabel('L4', 'Escalation')}</TableCell>
                            <TableCell style={{ backgroundColor: HEADER_BG, color: "#fff", fontWeight: "bold", textAlign: "center" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No Records Found</TableCell>
                            </TableRow>
                        ) : (
                            entries.map((entry, i) => (
                                <TableRow key={entry._id}>
                                    <TableCell align="center">{i + 1}</TableCell>
                                    <TableCell style={{ whiteSpace: "pre-line" }}>{entry.text}</TableCell>
                                    <TableCell style={{ backgroundColor: getTatColor('L1'), color: getContrastTextColor(getTatColor('L1')), textAlign: "center", fontWeight: 600 }}>
                                        {entry.l1User?.name || "-"}
                                    </TableCell>
                                    <TableCell style={{ backgroundColor: getTatColor('L2'), color: getContrastTextColor(getTatColor('L2')), textAlign: "center", fontWeight: 600 }}>
                                        {entry.l2User?.name || "-"}
                                    </TableCell>
                                    <TableCell style={{ backgroundColor: getTatColor('L3'), color: getContrastTextColor(getTatColor('L3')), textAlign: "center", fontWeight: 600 }}>
                                        {entry.l3User?.name || "-"}
                                    </TableCell>
                                    <TableCell style={{ backgroundColor: getTatColor('L4'), color: getContrastTextColor(getTatColor('L4')), textAlign: "center", fontWeight: 600 }}>
                                        {entry.l4User?.name || "-"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => openEditForm(entry)}>
                                            <EditIcon fontSize="small" style={{ color: "#1C375D" }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => confirmDelete(entry._id)}>
                                            <DeleteIcon fontSize="small" style={{ color: "#d32f2f" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Dialog */}
            <Dialog open={openForm} onClose={closeForm} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? "Edit Category" : "Add Category"}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Issue Type/Issue Category"
                        name="text"
                        value={formData.text}
                        onChange={handleChange}
                        margin="dense"
                        required
                        multiline
                        minRows={4}
                        placeholder={"a) Complimentary Items\nb) Referrals\nc) Team creation, ERP User Mapping & Deactivation"}
                    />
                    {renderUserSelect("L1 User (Primary Owner)", "l1User")}
                    {renderUserSelect("L2 User (Secondary Owner)", "l2User")}
                    {renderUserSelect("L3 User (Tertiary Owner)", "l3User")}
                    {renderUserSelect("L4 User (Escalation)", "l4User")}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeForm} color="secondary">Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!isFormValid}>
                        {editingId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation Dialog */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>Delete Category</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this category?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)} color="secondary">Cancel</Button>
                    <Button onClick={handleDelete} color="primary" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default connect(null, { setLoading, setSnackInfo })(EscalationMatrix);
